import { useState } from "react";

interface EstimateModalProps {
  open: boolean;
  onClose: () => void;
}

const FORM_URL = "https://readdy.ai/api/form/d6unhcd1l1ubpb1296v0";

const serviceOptions = [
  "Asphalt Paving",
  "Asphalt Repair & Maintenance",
  "Sealcoating",
  "Concrete Installation",
  "Concrete Repair",
  "Parking Lot Services",
  "Other",
];

const propertyTypes = ["Residential", "Commercial", "Industrial"];

export default function EstimateModal({ open, onClose }: EstimateModalProps) {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    property_type: "",
    project_description: "",
    address: "",
    timeline: "",
  });
  const [charCount, setCharCount] = useState(0);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "project_description") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus("submitting");
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([key, val]) => body.append(key, val));
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const handleClose = () => {
    setStep(1);
    setStatus("idle");
    setForm({ name: "", email: "", phone: "", service: "", property_type: "", project_description: "", address: "", timeline: "" });
    setCharCount(0);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-asphalt-800 border border-asphalt-600 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-asphalt-600">
          <div>
            <p className="font-body text-safety-500 text-xs uppercase tracking-widest font-600 mb-1">
              Free No-Obligation
            </p>
            <h2 className="font-heading text-white text-3xl tracking-wider">GET YOUR ESTIMATE</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center text-asphalt-300 hover:text-white text-2xl cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Progress */}
        {status === "idle" || status === "submitting" ? (
          <div className="px-6 pt-5 pb-0">
            <div className="flex items-center gap-2 mb-6">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 font-body transition-colors ${
                      step >= s ? "bg-safety-500 text-asphalt-900" : "bg-asphalt-600 text-asphalt-300"
                    }`}
                  >
                    {s}
                  </div>
                  <span className={`font-body text-xs uppercase tracking-wider font-600 ${step >= s ? "text-safety-500" : "text-asphalt-400"}`}>
                    {s === 1 ? "Your Info" : "Project Details"}
                  </span>
                  {s < 2 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? "bg-safety-500" : "bg-asphalt-600"}`} />}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Form */}
        {status === "success" ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-safety-500 rounded-full mx-auto mb-5">
              <i className="ri-check-line text-3xl text-asphalt-900" />
            </div>
            <h3 className="font-heading text-white text-3xl tracking-wider mb-3">Estimate Submitted!</h3>
            <p className="font-body text-asphalt-300 text-sm mb-8">
              We&apos;ve received your request and will be in touch within 24 hours. Questions? Call us directly.
            </p>
            <a
              href="tel:5558007283"
              className="inline-flex items-center gap-2 bg-safety-500 text-asphalt-900 font-body font-700 text-sm uppercase tracking-wider px-6 py-3 rounded-md cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              (555) 800-7283
            </a>
          </div>
        ) : status === "error" ? (
          <div className="p-10 text-center">
            <p className="font-body text-red-400 mb-4">Something went wrong. Please call us directly.</p>
            <a href="tel:5558007283" className="font-body text-safety-500 font-700 text-lg cursor-pointer">
              (555) 800-7283
            </a>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="p-6 flex flex-col gap-4"
          >
            {step === 1 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                      Full Name *
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                      Phone *
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors"
                      placeholder="(555) 000-0000"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Email *
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Project Address
                  </label>
                  <input
                    name="address"
                    type="text"
                    value={form.address}
                    onChange={handleChange}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors"
                    placeholder="123 Main St, Springfield, IL"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!form.name || !form.phone || !form.email}
                  className="w-full bg-safety-500 text-asphalt-900 font-body font-700 text-sm uppercase tracking-wider py-4 rounded-md hover:bg-safety-400 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap mt-2"
                >
                  Next: Project Details
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Service Needed *
                  </label>
                  <select
                    name="service"
                    required
                    value={form.service}
                    onChange={handleChange}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Property Type *
                  </label>
                  <select
                    name="property_type"
                    required
                    value={form.property_type}
                    onChange={handleChange}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select property type...</option>
                    {propertyTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Project Timeline
                  </label>
                  <select
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors cursor-pointer"
                  >
                    <option value="">When do you need this done?</option>
                    <option value="ASAP">As soon as possible</option>
                    <option value="1-2 Weeks">1–2 weeks</option>
                    <option value="1 Month">Within 1 month</option>
                    <option value="3 Months">Within 3 months</option>
                    <option value="Planning">Just planning ahead</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-asphalt-200 text-xs uppercase tracking-wider font-600 block mb-2">
                    Project Description ({charCount}/500)
                  </label>
                  <textarea
                    name="project_description"
                    value={form.project_description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full bg-asphalt-700 border border-asphalt-500 text-white text-sm font-body rounded-md px-4 py-3 focus:outline-none focus:border-safety-500 transition-colors resize-none"
                    placeholder="Describe your project, approximate size, current condition, etc."
                  />
                  {charCount > 500 && (
                    <p className="text-red-400 text-xs mt-1 font-body">Max 500 characters</p>
                  )}
                </div>
                <div className="flex gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 border border-asphalt-500 text-asphalt-200 font-body font-700 text-sm uppercase tracking-wider py-4 rounded-md hover:border-asphalt-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!form.service || !form.property_type || status === "submitting" || charCount > 500}
                    className="flex-1 bg-safety-500 text-asphalt-900 font-body font-700 text-sm uppercase tracking-wider py-4 rounded-md hover:bg-safety-400 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {status === "submitting" ? "Submitting..." : "Submit Estimate"}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
